import * as Mousetrap from 'mousetrap';
import type { RhythmicDuration, BaseDuration } from 'shared/definitions/musicTheory/rhythms';
import {
  assertValidRhythmicDuration,
  assertValidBaseDuration,
} from 'shared/definitions/musicTheory/rhythms';
import utils from '../utils';
import Rhythm from '../uTheory.Notation/rhythm';

import type { FontKey } from '../uTheory.Font/font';
import { findCharacter } from '../uTheory.Font/font';
import { html } from './rhythmPanel.tpl';

/*
  +----------------------------+
  | Breve | Whole     | Half   |
  +----------------------------+-----+
  | Quart | Eighth    | Sixt.  | Dot | // if no next row, or rests, trash & check all present.
  +----------------------------+-----+
  | Rests | Dot  | Trash    | Check  |
  +----------------------------------+
            ^^^-----> // if not present on 3rd line
*/

export interface RhythmPanelOptions {
  /* Whether to display a rest/note toggle, defaults false */
  rest: boolean;
  /* Whether to display a dot button, defalts false */
  dot: boolean;
  /* Whether to display a check button, defaults false */
  check: boolean;
  /* Whether to display a trash button, defaults false */
  trash: boolean;
  /* Whether to display an add note button, defaults false */
  addNote: boolean;
  /* Callback when the value changes */
  onChange?: (RhythmicDuration) => any;
  /* Callback when trash or check is pressed */
  onAction?: (buttonValue: 'trash' | 'check') => any;
  /* Color of not-pushed buttons */
  BGColor?: string;
  /* Color of pushed buttons */
  pushedBGColor?: string;
  /**
   * Whether to disable hotkeys, or what hot key to disable as an array. For instance
   * you might want to disable 'b' for 'breve' when using an accidental panel as well,
   * so you would pass ['b'].
   */
  disableHotKeys?: boolean | string[];
}

const baseDurationDictionary = {
  breve: 'b',
  whole: 'w',
  half: 'h',
  quarter: 'q',
  eighth: '8',
  sixteenth: '16',
};

function getRhythmNameFromBaseDuration(baseDuration: string): string {
  return Object.keys(baseDurationDictionary).find(
    (key) => baseDurationDictionary[key] === baseDuration
  );
}

/**
 * Create a rhythm button panel
 *
 * @param {HTMLDivElement} div in which to render content
 * @param {object} options
 * @param {boolean} options.rest display a rest/note toggle?
 * @param {boolean} options.dot display a dot/no-dot toggle?
 * @param {boolean} options.check display a check answer?
 * @param {boolean} options.trash display a trash can?
 * @param {boolean} options.addNote display an add note button?
 * @param {Function} options.onAny when any button is pressed
 * @param {Function} options.onChange when the value changes
 * @param {Function} options.onAction when trash or check is
 * @param {string} options.BGColor color of buttons when not pushed
 * @param {string} options.pushedBGColor color of buttons when pushed
 * @param {boolean|Array} options.disableHotKeys default false, include array of hotKeys to disable,
 * such as 'b' for 'breve' when using an accidental panel ('b' for 'flat') as well, or
 * such as 's' for 'simple' when your describing meter.
 *
 * @property {object} answer read only { rhythm, baseRhythm, isDotted, isRest }
 * @property {string} duration 'q' or 'q.'
 * @property {string} baseDuration 'q' -- rhythm without a dot
 * @property {boolean} isDotted
 * @property {boolean} isRest
 * @property {string} id the DOM id of the panel container.
 */
export default class RhythmPanel {
  options: RhythmPanelOptions;

  div: HTMLDivElement;

  protected lastRowNeeded: boolean;

  /* the outer div id of the template */
  id: string;

  constructor(div: HTMLDivElement, options: Partial<RhythmPanelOptions> = {}) {
    this.options = {
      rest: false,
      check: false,
      trash: false,
      addNote: false,
      dot: false,
      ...options,
    };

    this.div = div;
    const { rest, check, trash, addNote } = this.options;
    this.lastRowNeeded = rest || check || trash || addNote;

    const template = makeTemplate(options);
    div.innerHTML = template.template;
    this.id = template.id; // this the outer div id of template.
    setUpButtons(this);
    setUpHotKeys(this, options);
    const trashButton = div.querySelector('#fix-the-rhythm-panel-unicode');
    if (trashButton) {
      trashButton.innerHTML = String.fromCharCode(8224);
    }
  }

  reset() {
    this.clearAnswer();
    updateAddNote(this);
  }

  clearAnswer() {
    const div = this.div;
    (div.querySelector('#quarter') as HTMLInputElement).checked = true;
    (div.querySelector('#quarter') as HTMLInputElement).checked = false;
    this.showDots(false);
    this.showRests(false);
    updateAddNote(this);
  }

  showRests(bool: boolean) {
    const style = document.querySelector<HTMLDivElement>('.rhythm-panel-outer').style;
    (this.div.querySelector('#rest-toggle') as HTMLInputElement).checked = bool;
    style.setProperty('--rest-visibility', bool ? 'block' : 'none');
    style.setProperty('--note-visibility', bool ? 'none' : 'block');
    style.setProperty('--rest-selected', bool ? '1' : '0');
    updateAddNote(this);
  }

  showDots(bool: boolean) {
    (this.div.querySelector('#dot-toggle') as HTMLInputElement).checked = false;
    (this.div.querySelector('#last-row-dot-toggle') as HTMLInputElement).checked = false;
    const style = document.querySelector<HTMLDivElement>('.rhythm-panel-outer').style;
    style.setProperty('--dot-visibility', bool ? 'block' : 'none');
    updateAddNote(this);
  }

  exit() {
    this.div.innerHTML = '';
    Mousetrap.reset();
  }

  get duration(): RhythmicDuration {
    const baseDuration = this.baseDuration;
    const isDotted = this.isDotted;
    const duration = baseDuration + (isDotted ? '.' : '');
    assertValidRhythmicDuration(duration);
    return duration;
  }

  get rhythmicValueIsChecked(): boolean {
    const checkedEl = this.div.querySelector(
      'input[name="rhythm-panel-duration-radio"]:checked'
    ) as HTMLInputElement;
    return !!checkedEl;
  }

  get baseDuration(): BaseDuration {
    const checkedEl = this.div.querySelector(
      'input[name="rhythm-panel-duration-radio"]:checked'
    ) as HTMLInputElement | null;

    const baseDurationString: string = checkedEl ? checkedEl.value : 'q';
    const baseDuration = baseDurationDictionary[baseDurationString];
    assertValidBaseDuration(baseDuration);
    return baseDuration;
  }

  get isRest() {
    return (this.div.querySelector('#rest-toggle') as HTMLInputElement).checked;
  }

  get isDotted() {
    return (this.div.querySelector('#dot-toggle') as HTMLInputElement).checked;
  }

  get answer() {
    return {
      duration: this.duration,
      baseDuration: this.baseDuration,
      isRest: this.isRest,
      isDotted: this.isDotted,
    };
  }

  /**
   * Primarily for debugging -- simulates a click on an element.
   *
   * @param {string} keyValue the keyvalue associated with the button, see the HTML
   * template below for details.
   */
  click(baseDurationOrKeyValue: string) {
    const rhythmName = getRhythmNameFromBaseDuration(baseDurationOrKeyValue);
    if (rhythmName) {
      (this.div.querySelector(`#${rhythmName}`) as HTMLInputElement).click();
      return;
    }
    const el: HTMLInputElement = this.div.querySelector(`#${baseDurationOrKeyValue}`);
    if (el.style.display === 'none') return;
    if (el) {
      el.click();
    }
  }

  resize(height: number) {
    height = height || this.div.getBoundingClientRect().height;
    this.div.style.height = `${height}px`;

    const rows = this.lastRowNeeded ? 3 : 2;

    (this.div.querySelector('.rhythm-panel-outer') as HTMLDivElement).style.fontSize = `${~~(
      (height * 0.95) /
      rows
    )}px`;

    return 0;
  }

  /**
   */
  addNoteIsActive() {
    if (!this.options.addNote) return false;
    const baseDurationString = (
      this.div.querySelector(
        'input[name="rhythm-panel-duration-radio"]:checked'
      ) as HTMLInputElement
    ).value;
    return !!baseDurationString;
  }

  /**
   * Press the rhythm panel as is specified
   *
   * @param {Rhythm} rhythm
   * @param {boolean} isRest
   */
  setRhythm(rhythm: Rhythm, isRest = false) {
    this.showRests(isRest);
    this.showDots(!!rhythm.dots);
    const rhythmName = getRhythmNameFromBaseDuration(rhythm.baseDuration);
    (this.div.querySelector(`#${rhythmName}`) as HTMLInputElement).checked = true;
    updateAddNote(this);
  }
}

function makeTemplate(options: Partial<RhythmPanelOptions>) {
  const id = `rhythm-panel-${utils.uuid}`;

  const {
    rest = false,
    dot = false,
    check = false,
    trash = false,
    addNote = false,
    BGColor,
    pushedBGColor,
  } = options;

  const lastRowNeeded = rest || check || trash || addNote;
  const crowdedLastRow = (rest && check && trash) || addNote;
  const secondRowDot = dot && (!lastRowNeeded || crowdedLastRow);
  const thirdRowDot = dot && !secondRowDot;
  return {
    id,
    template: `
${html}

<style>
  .rhythm-panel-outer {
    --rest-visibility: none;
    --note-visibility: block;
    --dot-visibility: none;
    --third-row-visibility: ${lastRowNeeded ? 'flex' : 'none'};
    --second-row-dot-visibility: ${secondRowDot ? 'flex' : 'none'};
    --third-row-dot-visibility: ${thirdRowDot ? 'flex' : 'none'};
    --rest-toggle-visibility: ${rest ? 'flex' : 'none'};
    --trash-visibility: ${trash ? 'flex' : 'none'};
    --check-visibility: ${check ? 'flex' : 'none'};
    --add-note-visibility: ${addNote ? 'flex' : 'none'};
    --rest-selected: 0; /* 0 or 1 */
    --bg-color: ${BGColor || 'var(--button-color, #ffa127)'};
    --pushed-bg-color: ${pushedBGColor || 'var(--app-theme-contrast, #17aae7)'};
  }

</style>
`,
  };
}

function updateAddNote(rhythmPanel: RhythmPanel) {
  if (!rhythmPanel.options.addNote) return;
  if (!rhythmPanel.rhythmicValueIsChecked) {
    rhythmPanel.div.querySelector('#add-note + label span').innerHTML = '+4';
    rhythmPanel.div.querySelector('#aria-add-note-label').innerHTML = 'Add note';
    (rhythmPanel.div.querySelector('#add-note') as HTMLButtonElement).disabled = true;
    return;
  }
  (rhythmPanel.div.querySelector('#add-note') as HTMLButtonElement).disabled = false;

  const duration = rhythmPanel.duration + (rhythmPanel.isRest ? 'r' : '');
  const char = findCharacter('rhythm', duration as FontKey);
  rhythmPanel.div.querySelector('#add-note + label span').innerHTML = `+${char.string}`;
  const rhythm = new Rhythm(rhythmPanel.duration);
  let ariaString = 'Add ';
  ariaString += rhythm.toAriaString({ theWordNote: !rhythmPanel.isRest });
  ariaString += rhythmPanel.isRest ? ' rest' : '';
  rhythmPanel.div.querySelector('#aria-add-note-label').innerHTML = ariaString;
}

/**
 * Parcels out the results of an event a rhythm panel instance.
 *
 * @param {RhythmPanel} rhythmPanel
 * @param {HTMLElement} el
 */
function onEvent(rhythmPanel: RhythmPanel, el: HTMLInputElement | HTMLButtonElement) {
  const style = document.querySelector<HTMLDivElement>('.rhythm-panel-outer').style;
  if (el.id === 'rest-toggle') {
    const isChecked = (el as HTMLInputElement).checked;
    style.setProperty('--rest-visibility', isChecked ? 'block' : 'none');
    style.setProperty('--note-visibility', isChecked ? 'none' : 'block');
    style.setProperty('--rest-selected', isChecked ? '1' : '0');
    updateAddNote(rhythmPanel);
    return;
  }

  if (el.id === 'dot-toggle' || el.id === 'last-row-dot-toggle') {
    const display = (el as HTMLInputElement).checked ? 'block' : 'none';
    style.setProperty('--dot-visibility', display);
  }

  if (el instanceof HTMLButtonElement) {
    if (rhythmPanel.options.onAction) {
      rhythmPanel.options.onAction.bind(el)(el.id);
    }
    return;
  }

  if (rhythmPanel.options.onChange) {
    if (rhythmPanel.rhythmicValueIsChecked)
      rhythmPanel.options.onChange.bind(el)(rhythmPanel.duration);
  }
  updateAddNote(rhythmPanel);
}

function setUpButtons(rhythmPanel: RhythmPanel) {
  function eventCaller(evt) {
    evt.preventDefault();
    // this will be the element -- because this is called in context of the event.
    onEvent(rhythmPanel, this);
  }
  utils.nodeListForEach(rhythmPanel.div.querySelectorAll('input'), (el) => {
    el.addEventListener('change', eventCaller);
  });
  utils.nodeListForEach(rhythmPanel.div.querySelectorAll('button'), (el) => {
    el.addEventListener('click', eventCaller);
  });
}

function setUpHotKeys(
  rhythmPanel: RhythmPanel,
  { disableHotKeys }: { disableHotKeys?: boolean | string[] } = {}
) {
  if (disableHotKeys === true) return;
  const valueToHotKeys = {
    'dot-toggle': ['.'],
    breve: ['b', '1'],
    whole: ['w', '2'],
    half: ['h', '3'],
    quarter: ['q', '4'],
    eighth: ['e', '5'],
    sixteenth: ['s', '6'],
    trash: ['del', 'backspace'],
    check: ['enter'],
    'add-note': ['+'],
  };

  if (utils.isArray(disableHotKeys)) {
    Object.keys(valueToHotKeys).forEach((key) => {
      valueToHotKeys[key] = valueToHotKeys[key].filter(
        (hotKey) => !disableHotKeys.includes(hotKey)
      );
    });
  }

  Object.keys(valueToHotKeys).forEach((elementId) => {
    const el = rhythmPanel.div.querySelector(`#${elementId}`) as
      | HTMLInputElement
      | HTMLButtonElement;
    const hotKeys = valueToHotKeys[elementId].filter(
      (hotKey) => !disableHotKeys || !disableHotKeys.includes(hotKey)
    );
    if (el) {
      Mousetrap.bind(hotKeys, () => {
        el.click();
      });
    } else {
      console.error(`Didn't get element for ${elementId}`);
    }
  });
}
